/**
 * ACTIVITY SECTIONS — fonte di verità unica per la corrispondenza tra:
 *  - tipo attività della timeline (ActivityItem.type, es. 'nota')
 *  - segmento URL / sotto-tab (slug, es. 'notes')
 *  - prefisso dell'id item in ActivityTimeline (es. 'note' -> 'note:123';
 *    il Task fa eccezione e usa l'id numerico grezzo)
 *  - FQN Java dell'entità (simple name, es. 'Note')
 *
 * Usata da:
 *  - useEntityActivities per GENERARE gli id degli item (evita drift dei prefissi)
 *  - le detail (Persona/Azienda/Preventivo) per URL deep-linkabili dei sotto-tab
 *    e per il focus sull'attività (/contacts/:id/:section/:activityId/comments/:commentId)
 *  - ActivityTools per costruire il sourcePath del link nella mail di menzione
 *
 * NB: 'linkedin' ha uno slug ma NON è un filtro-tab (filterable=false): le attività
 * LinkedinPost vivono solo nella vista "Tutte". Un deep-link linkedin atterra quindi
 * su "Tutte" (nessun tab attivo) ma espande comunque il post.
 */

interface ActivityKind {
  /** Simple name dell'FQN Java (es. 'Note' di it.dvel.crm.core.model.Note). */
  simpleName: string;
  /** Segmento URL del sotto-tab (inglese plurale). */
  slug: string;
  /** ActivityItem.type usato dalla timeline (italiano). */
  type: string;
  /** Prefisso dell'id item in timeline; null = id numerico grezzo (Task). */
  idPrefix: string | null;
  /** true se compare come tab-filtro nella striscia. LinkedinPost no. */
  filterable: boolean;
  /**
   * Posizione nella striscia dei tipi, per i soli `filterable`. Numerati di CENTO
   * in CENTO di proposito: fra due voci restano novantanove posizioni libere, e un
   * plugin che contribuisce una tab (slot `detail-center:activity-tab`) può
   * metterla IN MEZZO invece che solo in coda. Stesso schema dello slot
   * `detail-left:info` in @crm/contracts. Il core non riserva intervalli a
   * nessuno e non sa quali valori sceglieranno i plugin.
   */
  order?: number;
}

// L'ordine di questo array NON è quello della striscia: quello lo dà `order`.
// Qui le voci stanno in ordine di dominio, la striscia le riordina.
const ACTIVITY_KINDS: readonly ActivityKind[] = [
  { simpleName: 'Task', slug: 'tasks', type: 'task', idPrefix: null, filterable: true, order: 500 },
  { simpleName: 'Note', slug: 'notes', type: 'nota', idPrefix: 'note', filterable: true, order: 100 },
  { simpleName: 'Call', slug: 'calls', type: 'chiamata', idPrefix: 'call', filterable: true, order: 300 },
  { simpleName: 'Email', slug: 'emails', type: 'email', idPrefix: 'email', filterable: true, order: 200 },
  { simpleName: 'Meeting', slug: 'meetings', type: 'riunione', idPrefix: 'meeting', filterable: true, order: 400 },
  { simpleName: 'LinkedinPost', slug: 'linkedin', type: 'linkedin', idPrefix: 'linkedin', filterable: false },
  { simpleName: 'WhatsAppMessage', slug: 'whatsapp', type: 'whatsapp', idPrefix: 'whatsapp', filterable: false },
];

/**
 * Le voci che compaiono come tab, già ordinate. È l'UNICA origine della striscia:
 * prima l'elenco viveva anche in `ACTIVITY_FILTERS` dentro useEntityActivities, e
 * i due potevano dire cose diverse — di fatto `filterable` era una dichiarazione
 * che nessuno leggeva, perché l'ordine e la composizione li decideva l'altro array.
 */
export const ACTIVITY_TAB_KINDS: readonly { type: string; slug: string; order: number }[] =
  ACTIVITY_KINDS
    .filter((k) => k.filterable)
    .map((k) => ({ type: k.type, slug: k.slug, order: k.order ?? 0 }))
    .sort((a, b) => a.order - b.order);

/** Solo i tipi, nell'ordine della striscia. Comodo per chi filtra e basta. */
export const ACTIVITY_FILTER_TYPES: readonly string[] = ACTIVITY_TAB_KINDS.map((k) => k.type);

const bySlug = new Map(ACTIVITY_KINDS.map((k) => [k.slug, k]));
const byType = new Map(ACTIVITY_KINDS.map((k) => [k.type, k]));
const bySimpleName = new Map(ACTIVITY_KINDS.map((k) => [k.simpleName, k]));

/** Estrae il simple name da un FQN Java (o restituisce l'input se già semplice). */
function toSimpleName(fqnOrSimpleName: string): string {
  const idx = fqnOrSimpleName.lastIndexOf('.');
  return idx >= 0 ? fqnOrSimpleName.slice(idx + 1) : fqnOrSimpleName;
}

/** Slug valido (incluso 'linkedin')? Usato dalle detail per il redirect a "Tutte". */
export function isValidActivitySection(section: string | undefined): boolean {
  return section != null && bySlug.has(section);
}

/**
 * Slug URL -> activeFilters della timeline. [] = "Tutte" (slug ignoto o non
 * filtrabile, es. 'linkedin').
 */
export function sectionToActiveFilters(section: string | undefined): string[] {
  const kind = section != null ? bySlug.get(section) : undefined;
  return kind && kind.filterable ? [kind.type] : [];
}

/** activeFilters della timeline -> slug URL (undefined = "Tutte"). */
export function activeFiltersToSection(filters: string[]): string | undefined {
  if (filters.length !== 1) return undefined;
  return byType.get(filters[0])?.slug;
}

/** FQN/simple name -> slug del sotto-tab (undefined se sconosciuto). */
export function fqnToSection(fqnOrSimpleName: string): string | undefined {
  return bySimpleName.get(toSimpleName(fqnOrSimpleName))?.slug;
}

/**
 * (slug, primary key) -> id dell'item timeline, per il focus dell'attività
 * dall'URL. Es. ('notes', 123) -> 'note:123'; ('tasks', 9) -> '9'.
 * Restituisce null se lo slug è sconosciuto.
 */
export function sectionToItemId(section: string | undefined, pk: number | string): string | null {
  const kind = section != null ? bySlug.get(section) : undefined;
  if (!kind) return null;
  return kind.idPrefix ? `${kind.idPrefix}:${pk}` : String(pk);
}
